// ==============================
// Merge duplicate customer accounts (User Management → Group tab)
// ==============================
// The admin selects 2+ accounts in a group, picks which one survives (primary),
// and confirms. Services (registrations, trial bookings, intake responses and
// conversions) from the other accounts are reassigned to the primary and those
// accounts are archived. Backed by POST /user/merge.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { apiPost } from '../../utils/apiClient';
import { User } from '../../types/UserTable.type';
import { userTypeColors } from '../../utils/colorHandler';
import ColorChip from '../common/ColorChip';

interface MergeProfileDialogProps {
  open: boolean;
  onClose: () => void;
  /** The accounts the admin selected to merge (2 or more). */
  users: User[];
  /** Called after a successful merge so the caller can refresh + reset. */
  onMerged: () => void;
}

const displayName = (user: User): string =>
  user.preferredName?.trim() ||
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
  `#${user.id}`;

const MergeProfileDialog: React.FC<MergeProfileDialogProps> = ({
  open,
  onClose,
  users,
  onMerged,
}) => {
  // Default the primary to the lowest id (the earliest-created account).
  const defaultPrimary =
    users.length > 0
      ? users.reduce((lowest, user) => (user.id < lowest.id ? user : lowest))
          .id
      : null;

  const [primaryId, setPrimaryId] = useState<number | null>(defaultPrimary);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset the choice whenever the dialog is opened with a new selection.
  // (Intentionally keyed on `open` only; `defaultPrimary` is the seed value.)
  useEffect(() => {
    if (open) {
      setPrimaryId(defaultPrimary);
      setError('');
    }
  }, [open]);

  const handleMerge = async () => {
    if (primaryId === null) return;
    const mergeIds = users
      .map((user) => user.id)
      .filter((id) => id !== primaryId);
    if (mergeIds.length === 0) return;

    setSaving(true);
    setError('');
    try {
      await apiPost('/user/merge', { primaryId, mergeIds });
      onMerged();
      onClose();
    } catch (err) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Unable to merge the selected accounts.',
      );
    } finally {
      setSaving(false);
    }
  };

  const mergedCount = users.filter((user) => user.id !== primaryId).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>
          Merge accounts
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose the account to keep. The others will be archived and all their
          services (registrations, trial bookings, form responses) moved to it.
          This cannot be undone automatically.
        </Typography>

        <RadioGroup
          value={primaryId ?? ''}
          onChange={(event) => setPrimaryId(Number(event.target.value))}
        >
          <Stack spacing={1}>
            {users.map((user) => (
              <FormControlLabel
                key={user.id}
                value={user.id}
                control={<Radio />}
                sx={{
                  m: 0,
                  border: '1px solid',
                  borderColor:
                    user.id === primaryId ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
                label={
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography variant="body2" fontWeight={700}>
                      {displayName(user)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{user.id}
                    </Typography>
                    <ColorChip label={user.userType} palette={userTypeColors} />
                    {user.phone && (
                      <Typography variant="caption" color="text.secondary">
                        {user.phone}
                      </Typography>
                    )}
                    {user.email && (
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    )}
                    {user.id === primaryId && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'primary.main',
                        }}
                      >
                        KEEP
                      </Box>
                    )}
                  </Stack>
                }
              />
            ))}
          </Stack>
        </RadioGroup>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleMerge}
          disabled={saving || primaryId === null || mergedCount === 0}
        >
          {saving
            ? 'Merging…'
            : `Merge ${mergedCount} account${mergedCount === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeProfileDialog;
